import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { shallowEqual } from "react-redux";
import Select from "react-select";
import styles from "./styles.module.scss";
import ModalLayout from "app/components/Layouts/ModalLayout/ModalLayout";
import { useActions, useSelector } from "app/utils/hooks";
import { clear, fetchTimezone } from "app/actions/timezone";
import { editProfileSettings, fetchSelf } from "app/actions/user";
import { RootState } from "typedefs";

interface TimezoneOption {
  value: string;
  label: string;
}

interface TimezonePopupModalProps {
  isOpen: boolean;
  currentTimezone?: string;
  locale?: string;
  onClose: () => void;
}

function formatOffset(offsetSeconds: number) {
  const decimal = offsetSeconds / 3600;
  const sign = decimal < 0 ? "-" : "+";
  const hour = Math.floor(Math.abs(decimal));
  const min = Math.floor((Math.abs(decimal) * 60) % 60);

  return (
    sign + (hour < 10 ? "0" : "") + hour + ":" + (min < 10 ? "0" : "") + min
  );
}

const TimezonePopupModal = ({
  isOpen,
  currentTimezone,
  locale,
  onClose,
}: TimezonePopupModalProps) => {
  const { t } = useTranslation();
  const timezones = useSelector(
    (state: RootState) => state.timezone.data,
    shallowEqual,
  );
  const timezoneActions = useActions({ fetchTimezone, clear });
  const userActions = useActions({ editProfileSettings, fetchSelf });

  const [selected, setSelected] = useState(currentTimezone ?? "");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    timezoneActions.fetchTimezone();
    return () => {
      timezoneActions.clear();
    };
  }, [isOpen]);

  useEffect(() => {
    setSelected(currentTimezone ?? "");
  }, [currentTimezone]);

  const options = useMemo<TimezoneOption[]>(() => {
    if (!timezones) {
      return [];
    }

    return timezones.map(
      (tz: { name: string; displayName?: string; utcTotalOffset: number }) => {
        const iana = tz.name.includes("Translation missing")
          ? tz.name.split(".").pop() || tz.name
          : tz.name;
        let displayName = tz.displayName ?? iana;

        if (locale === "ja") {
          try {
            const parts = new Intl.DateTimeFormat("ja-JP", {
              timeZone: iana,
              timeZoneName: "long",
            }).formatToParts(new Date());
            const localized = parts.find((p) => p.type === "timeZoneName")?.value;
            if (localized) {
              displayName = localized;
            }
          } catch {
            // fall through with the API-provided displayName
          }
        }

        return {
          value: iana,
          label: `${displayName} (${iana} ${formatOffset(tz.utcTotalOffset)})`,
        };
      },
    );
  }, [timezones, locale]);

  const selectedOption = useMemo(
    () => options.find((opt) => opt.value === selected) ?? null,
    [options, selected],
  );

  const handleSave = async () => {
    setSubmitting(true);
    try {
      const updates: { [key: string]: any } = { timezone_popup_shown: true };
      if (selected && selected !== currentTimezone) {
        updates.timezone = selected;
      }
      await userActions.editProfileSettings(updates);
      await userActions.fetchSelf(true);
      onClose();
    } catch (err) {
      console.error("Failed to save timezone confirmation", err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalLayout
      isOpen={isOpen}
      onClose={onClose}
      classNames={styles.modal_container}
    >
      <div className={styles.text_content}>
        <h2 className={styles.title}>{t("Check your timezone")}</h2>
        <p className={styles.description}>
          {t(
            "Please confirm that your timezone is set correctly to ensure accurate dates and times.",
          )}
        </p>
      </div>
      <div className={styles.field}>
        <label htmlFor="timezone-popup-select" className={styles.label}>
          {t("Timezone")}
        </label>
        <Select
          inputId="timezone-popup-select"
          className={styles.select}
          classNamePrefix="select-box"
          isClearable={false}
          isMulti={false}
          options={options}
          value={selectedOption}
          isDisabled={!options.length || submitting}
          placeholder={t("Select timezone")}
          menuPortalTarget={typeof document !== "undefined" ? document.body : null}
          menuPosition="fixed"
          styles={{
            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
          }}
          onChange={(opt: TimezoneOption | null) => {
            if (opt) {
              setSelected(opt.value);
            }
          }}
        />
      </div>
      <button
        className={styles.cta_button}
        onClick={handleSave}
        disabled={submitting || !options.length}
      >
        {submitting ? t("Saving...") : t("Confirm")}
      </button>
    </ModalLayout>
  );
};

export default TimezonePopupModal;
